import type { InkDocumentV1 } from '../../types/ink';
import type { StudyActivityTimerStateV2 } from './studyActivityTimer';
import type { MathAnswerDraftMapV2, MathAnswerDraftV2 } from './answerCapture';
import type { ProblemActivityPersistedStateV2 } from './problemActivity';

export const LE_V2_STUDY_DRAFT_CONTRACT_VERSION = 'le-v2-study-draft.1' as const;

export type StudyDraftPhaseV2 = 'study' | 'correction' | 'completed';

export type StudyDraftBindingV2 = {
  studySessionId: string;
  assignmentId: string;
  worksheetVersionId: string;
};

export type StudyDraftV2 = {
  schemaVersion: 2;
  contractVersion: typeof LE_V2_STUDY_DRAFT_CONTRACT_VERSION;
  savedAt: string;
  phase: StudyDraftPhaseV2;
  binding: StudyDraftBindingV2;
  pageIndex: number;
  answersByProblemId: MathAnswerDraftMapV2;
  studentInkByPage: Record<string, InkDocumentV1>;
  frozenFirstPassInkByPage: Record<string, InkDocumentV1>;
  correctionAnswersByQuestionId: MathAnswerDraftMapV2;
  correctionInkByPage: Record<string, InkDocumentV1>;
  problemTiming: ProblemActivityPersistedStateV2;
  correctionTiming: ProblemActivityPersistedStateV2;
  sessionTimer: StudyActivityTimerStateV2;
};

export type StudyDraftStorageV2 = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const KEY_PREFIX = 'belajarku:study-v2:draft';

function cleanId(value: string) {
  return encodeURIComponent(value.trim());
}

export function studyDraftStorageKeyV2(binding: StudyDraftBindingV2) {
  return `${KEY_PREFIX}:${cleanId(binding.assignmentId)}:${cleanId(binding.worksheetVersionId)}:${cleanId(binding.studySessionId)}`;
}

function sameBinding(a: StudyDraftBindingV2, b: StudyDraftBindingV2) {
  return a.studySessionId === b.studySessionId &&
    a.assignmentId === b.assignmentId &&
    a.worksheetVersionId === b.worksheetVersionId;
}

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function validAnswerDraft(value: unknown): value is MathAnswerDraftV2 {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Record<string, unknown>;
  if (typeof draft.rawValue !== 'string') return false;
  if (draft.response !== null) {
    if (!draft.response || typeof draft.response !== 'object') return false;
    const response = draft.response as Record<string, unknown>;
    if (response.type !== 'integer' || typeof response.value !== 'number' || !Number.isSafeInteger(response.value)) return false;
  }
  for (const key of ['firstInteractedAt', 'lastEditedAt'] as const) {
    if (draft[key] !== undefined && typeof draft[key] !== 'string') return false;
  }
  return true;
}

function validAnswerMap(value: unknown): value is MathAnswerDraftMapV2 {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) &&
    Object.values(value as Record<string, unknown>).every(validAnswerDraft));
}

function validInkDocument(value: unknown): value is InkDocumentV1 {
  if (!value || typeof value !== 'object') return false;
  const ink = value as Record<string, unknown>;
  if (ink.version !== 1 || !finiteNonNegative(ink.width) || ink.width <= 0 || !finiteNonNegative(ink.height) || ink.height <= 0) return false;
  if (!Array.isArray(ink.strokes)) return false;
  return ink.strokes.every((rawStroke) => {
    if (!rawStroke || typeof rawStroke !== 'object') return false;
    const stroke = rawStroke as Record<string, unknown>;
    if (typeof stroke.id !== 'string' || typeof stroke.pointerType !== 'string' || !Array.isArray(stroke.points)) return false;
    return stroke.points.every((rawPoint) => {
      if (!rawPoint || typeof rawPoint !== 'object') return false;
      const point = rawPoint as Record<string, unknown>;
      return typeof point.x === 'number' && Number.isFinite(point.x) &&
        typeof point.y === 'number' && Number.isFinite(point.y) &&
        typeof point.t === 'number' && Number.isFinite(point.t) &&
        (point.pressure === undefined || (typeof point.pressure === 'number' && Number.isFinite(point.pressure)));
    });
  });
}

function validInkMap(value: unknown): value is Record<string, InkDocumentV1> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) &&
    Object.values(value as Record<string, unknown>).every(validInkDocument));
}

function validProblemTimingState(value: unknown): value is ProblemActivityPersistedStateV2 {
  if (!value || typeof value !== 'object') return false;
  const records = (value as Record<string, unknown>).records;
  if (!records || typeof records !== 'object' || Array.isArray(records)) return false;
  return Object.values(records as Record<string, unknown>).every((raw) => {
    if (!raw || typeof raw !== 'object') return false;
    const record = raw as Record<string, unknown>;
    return finiteNonNegative(record.firstAtMs) && finiteNonNegative(record.activeAccumulatedMs) && finiteNonNegative(record.lastAtMs);
  });
}

function validTimerState(value: unknown): value is StudyActivityTimerStateV2 {
  if (!value || typeof value !== 'object') return false;
  const timer = value as Record<string, unknown>;
  return finiteNonNegative(timer.startedAtMs) && finiteNonNegative(timer.activeAccumulatedMs) &&
    finiteNonNegative(timer.pausedAccumulatedMs) && finiteNonNegative(timer.savedAtMs) &&
    typeof timer.wasPaused === 'boolean';
}

export function validateStudyDraftV2(value: unknown, expectedBinding?: StudyDraftBindingV2): value is StudyDraftV2 {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Record<string, unknown>;
  if (draft.schemaVersion !== 2 || draft.contractVersion !== LE_V2_STUDY_DRAFT_CONTRACT_VERSION) return false;
  if (typeof draft.savedAt !== 'string' || !Number.isFinite(Date.parse(draft.savedAt))) return false;
  if (draft.phase !== 'study' && draft.phase !== 'correction' && draft.phase !== 'completed') return false;
  if (!draft.binding || typeof draft.binding !== 'object') return false;
  const binding = draft.binding as StudyDraftBindingV2;
  if (![binding.studySessionId, binding.assignmentId, binding.worksheetVersionId].every((id) => typeof id === 'string' && id.length > 0)) return false;
  if (expectedBinding && !sameBinding(binding, expectedBinding)) return false;
  if (!Number.isInteger(draft.pageIndex) || Number(draft.pageIndex) < 0) return false;
  if (!validAnswerMap(draft.answersByProblemId) || !validAnswerMap(draft.correctionAnswersByQuestionId)) return false;
  if (!validInkMap(draft.studentInkByPage) || !validInkMap(draft.frozenFirstPassInkByPage) || !validInkMap(draft.correctionInkByPage)) return false;
  if (!validProblemTimingState(draft.problemTiming) || !validProblemTimingState(draft.correctionTiming) || !validTimerState(draft.sessionTimer)) return false;
  return true;
}

export function loadStudyDraftV2(storage: StudyDraftStorageV2, binding: StudyDraftBindingV2): StudyDraftV2 | null {
  try {
    const raw = storage.getItem(studyDraftStorageKeyV2(binding));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return validateStudyDraftV2(parsed, binding) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveStudyDraftV2(storage: StudyDraftStorageV2, draft: StudyDraftV2): boolean {
  if (!validateStudyDraftV2(draft, draft.binding)) throw new Error('INVALID_STUDY_DRAFT');
  try {
    storage.setItem(studyDraftStorageKeyV2(draft.binding), JSON.stringify(draft));
    return true;
  } catch {
    // Storage quota/private-mode failures must never crash the Study surface.
    return false;
  }
}

export function removeStudyDraftV2(storage: StudyDraftStorageV2, binding: StudyDraftBindingV2) {
  storage.removeItem(studyDraftStorageKeyV2(binding));
}

export function cloneInkMapV2(input: Record<string, InkDocumentV1>): Record<string, InkDocumentV1> {
  return Object.fromEntries(Object.entries(input).map(([pageId, document]) => [pageId, {
    ...document,
    strokes: document.strokes.map((stroke) => ({
      ...stroke,
      points: stroke.points.map((point) => ({ ...point })),
    })),
  }]));
}
